import { Price, ProductDuration } from "./support"
import { getCheckoutSessionEcommerce } from "./backend"

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>
  }
}

export const STRIPE_CHECKOUT_EXPERIMENT_ID = "stripe_checkout"

export interface EcommerceExperimentParams {
  experiment_id: string
  experiment_variant: string
}

let ecommerceExperimentParams: EcommerceExperimentParams | null = null

// While an experiment is running, its id/variant ride on every ecommerce
// event as event parameters, so GA4 can split purchases and revenue by the
// experiment_id / experiment_variant custom dimensions.
export function setEcommerceExperimentParams(params: EcommerceExperimentParams | null): void {
  ecommerceExperimentParams = params
}

const SUPPORTED_UTM_KEYS = [
  "utm_medium",
  "utm_source",
  "utm_campaign",
  "utm_id",
  "utm_content",
  "utm_term",
] as const

const pushedEvents = new Set<string>()

function dataLayer(): Array<Record<string, unknown>> {
  window.dataLayer = window.dataLayer || []
  return window.dataLayer
}

function utmParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search)

  return SUPPORTED_UTM_KEYS.reduce<Record<string, string>>((memo, key) => {
    const value = params.get(key)
    if (!value) return memo

    return {
      ...memo,
      [key]: value,
    }
  }, {})
}

function amountFromMinorUnits(amount: number): number {
  return amount / 100
}

function buildItems(
  subscription: ProductDuration,
  price: Price,
  amountMinorUnits: number,
): Array<Record<string, unknown>> {
  return [
    {
      item_id: price.price_id,
      item_name: "Surf-Forecast Membership",
      item_category: "subscription",
      item_variant: subscription,
      price: amountFromMinorUnits(amountMinorUnits),
      quantity: 1,
    },
  ]
}

function pushEvent(event: string, params: Record<string, unknown> = {}): void {
  dataLayer().push({
    event,
    ...utmParams(),
    ...params,
  })
}

function pushEcommerceEvent(
  event: string,
  ecommerce: Record<string, unknown>,
  params: Record<string, unknown> = {},
): void {
  dataLayer().push({ ecommerce: null })
  pushEvent(event, {
    ...(ecommerceExperimentParams || {}),
    ...params,
    ecommerce,
  })
}

function pushOnce(key: string, callback: () => void): void {
  if (pushedEvents.has(key)) return

  pushedEvents.add(key)
  callback()
}

export function trackSignUp(method = "email"): void {
  pushEvent("sign_up", { method })
}

export function trackLogin(method = "email"): void {
  pushEvent("login", { method })
}

export function trackBeginCheckout(
  subscription: ProductDuration,
  price: Price,
  amountMinorUnits: number,
  coupon?: string,
): void {
  pushOnce(`begin_checkout:${price.price_id}:${amountMinorUnits}:${coupon || ""}`, () => {
    pushEcommerceEvent("begin_checkout", {
      currency: price.currency.toUpperCase(),
      value: amountFromMinorUnits(amountMinorUnits),
      coupon,
      items: buildItems(subscription, price, amountMinorUnits),
    })
  })
}

export function trackAddPaymentInfo(
  subscription: ProductDuration,
  price: Price,
  amountMinorUnits: number,
  paymentType: string,
  coupon?: string,
): void {
  pushEcommerceEvent("add_payment_info", {
    currency: price.currency.toUpperCase(),
    value: amountFromMinorUnits(amountMinorUnits),
    coupon,
    payment_type: paymentType,
    items: buildItems(subscription, price, amountMinorUnits),
  })
}

export function trackPurchase(
  transactionId: string,
  subscription: ProductDuration,
  price: Price,
  amountMinorUnits: number,
  coupon?: string,
): void {
  pushOnce(`purchase:${transactionId}`, () => {
    pushEcommerceEvent("purchase", {
      transaction_id: transactionId,
      currency: price.currency.toUpperCase(),
      value: amountFromMinorUnits(amountMinorUnits),
      coupon,
      items: buildItems(subscription, price, amountMinorUnits),
    })
  })
}

function checkoutPurchaseAlreadyTracked(storageKey: string): boolean {
  try {
    return !!window.localStorage.getItem(storageKey)
  } catch (error) {
    return false
  }
}

function storeCheckoutPurchaseTracked(storageKey: string): void {
  try {
    window.localStorage.setItem(storageKey, new Date().toISOString())
  } catch (error) {
    // localStorage unavailable (e.g. blocked cookies); pushOnce still dedupes
    // within this pageview.
  }
}

export type CheckoutPurchaseOutcome = "recorded" | "already_recorded" | "pending" | "error"

// The Checkout-arm purchase is reported when the visitor returns from the
// Stripe-hosted page. The payload is server-computed from the checkout
// session (including the experiment arm recorded in its Stripe metadata).
// The return URL can be reloaded, so a localStorage guard backs up the
// per-pageview pushOnce dedupe, keyed by the checkout session id. The
// returned outcome lets the caller pick the screen and retry on failures.
export async function trackPurchaseForCheckoutSession(
  sessionId: string,
): Promise<CheckoutPurchaseOutcome> {
  const storageKey = `ga4-purchase:${sessionId}`
  if (checkoutPurchaseAlreadyTracked(storageKey)) return "already_recorded"

  const result = await getCheckoutSessionEcommerce(sessionId)
  if (result.state !== "paid") return result.state

  const { payload } = result
  const transactionId = String(payload.ecommerce.transaction_id || sessionId)
  pushOnce(`purchase:${transactionId}`, () => {
    const params: Record<string, unknown> = {}
    if (payload.experiment_id && payload.experiment_variant) {
      params.experiment_id = payload.experiment_id
      params.experiment_variant = payload.experiment_variant
    }
    pushEcommerceEvent("purchase", payload.ecommerce, params)
    storeCheckoutPurchaseTracked(storageKey)
  })
  return "recorded"
}

export function trackPromoCodeOpen(): void {
  pushEvent("promo_code_open")
}

export function trackPromoCodeApplied(params: Record<string, unknown> = {}): void {
  pushEvent("promo_code_applied", params)
}
